import { useState, useEffect, useRef } from 'react';
import { User, ChevronDown, Check, Search } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';
import { getExternalPic } from '../../../services/projectReportServices';

const avatarBg = ['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6'];

const ExternalPicDropdown = ({ taskId, value, onChange, accent, isReadOnly, cellInputStyle }) => {
  const [isOpen, setIsOpen]     = useState(false);
  const [search, setSearch]     = useState('');
  const [options, setOptions]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const dropdownRef             = useRef(null);

  const defaultStyle = {
    border: '1.5px solid #e2e8f0', borderRadius: '8px',
    padding: '6px 10px', fontSize: '12px', color: '#0f172a',
    background: '#fafafa', cursor: 'pointer', outline: 'none',
    width: '100%', transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: "'Inter', sans-serif",
  };
  const btnStyle = cellInputStyle || defaultStyle;

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadOptions = async (keyword = '') => {
    setLoading(true);
    try {
      const data = await getExternalPic(keyword || null);
      setOptions(data || []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    if (isReadOnly) return;
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      setSearch('');
      loadOptions('');
    }
  };

  const handleSearch = (val) => {
    setSearch(val);
    loadOptions(val);
  };

  const handleSelect = (item) => {
    onChange(item ? `${item.kode}-${item.name}-${item.contact}` : '');
    setIsOpen(false);
  };

  const displayName = value ? value.split('-').slice(1).join('-') || value : null;

  const getInitial = (name) => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    return words.length >= 2
      ? (words[0][0] + words[1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };
  const getAvatarBg = (name) => avatarBg[(name?.charCodeAt(0) || 0) % avatarBg.length];

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <Tooltip
        target={`.ext-pic-tooltip-${taskId}`}
        position="top"
        mouseTrack
        mouseTrackTop={15}
        pt={{ text: { style: { fontSize: '11px', padding: '6px 10px', fontWeight: '500', maxWidth: '260px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } } }}
      />
      <button
        type="button"
        className={`ext-pic-tooltip-${taskId}`}
        data-pr-tooltip={displayName || 'Klik untuk mengubah PIC External'}
        onClick={handleOpen}
        style={{
          ...btnStyle,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderColor: isOpen ? accent : '#e2e8f0',
          boxShadow: isOpen ? `0 0 0 3px ${accent}20` : 'none',
          color: value ? '#0f172a' : '#94a3b8',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
          <User size={13} color={value ? accent : '#94a3b8'} style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '11.5px', fontWeight: value ? '600' : '400' }}>
            {displayName || '— Pilih PIC External'}
          </span>
        </div>
        <ChevronDown size={12} color="#94a3b8" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
            zIndex: 350, background: '#fff', borderRadius: '12px',
            border: '1.5px solid #e2e8f0', boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
            overflow: 'hidden', animation: 'dropFadeIn 0.15s ease',
            display: 'flex', flexDirection: 'column',
            maxHeight: '280px', width: '260px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={12} color="#94a3b8" style={{ position: 'absolute', left: '10px' }} />
              <input
                autoFocus
                type="text"
                placeholder="Cari PIC External..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  width: '100%', padding: '6px 10px 6px 28px',
                  border: '1px solid #e2e8f0', borderRadius: '6px',
                  fontSize: '11.5px', outline: 'none', color: '#0f172a',
                  fontFamily: "'Inter', sans-serif",
                }}
                onFocus={(e) => { e.target.style.borderColor = accent; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
              />
            </div>
          </div>

          {/* Options */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '4px' }}>
            <Tooltip
              target=".ext-pic-option-tooltip"
              position="right"
              mouseTrack
              mouseTrackTop={15}
              pt={{ text: { style: { fontSize: '11px', padding: '6px 10px', fontWeight: '500', maxWidth: '260px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } } }}
            />
            {loading ? (
              <div style={{ padding: '16px', textAlign: 'center', fontSize: '11.5px', color: '#94a3b8' }}>
                Memuat...
              </div>
            ) : (
              <>
                {/* Clear option */}
                {value && (
                  <div
                    onClick={() => handleSelect(null)}
                    style={{
                      padding: '8px 10px', fontSize: '12px', color: '#94a3b8',
                      cursor: 'pointer', borderRadius: '6px',
                      display: 'flex', alignItems: 'center', gap: '8px',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#94a3b8', flexShrink: 0 }}>—</div>
                    <span style={{ fontSize: '12px' }}>— Kosongkan —</span>
                  </div>
                )}

                {options.map((item, i) => {
                  const picVal = `${item.kode}-${item.name}`;
                  const isSelected = value === picVal;
                  return (
                    <div
                      key={i}
                      className="ext-pic-option-tooltip"
                      data-pr-tooltip={`${item.name}\n${item.kode} · ${item.contact}`}
                      onClick={() => handleSelect(item)}
                      style={{
                        padding: '8px 10px', cursor: 'pointer', borderRadius: '6px',
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: isSelected ? `${accent}15` : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: getAvatarBg(item.name),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '11px', fontWeight: 'bold', flexShrink: 0,
                      }}>
                        {getInitial(item.name)}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12.5px', fontWeight: isSelected ? '700' : '600', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.name}
                        </div>
                        <div style={{ fontSize: '10.5px', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.kode} · {item.contact}
                        </div>
                      </div>

                      {isSelected && <Check size={12} color={accent} style={{ flexShrink: 0 }} />}
                    </div>
                  );
                })}

                {options.length === 0 && !loading && (
                  <div style={{ padding: '12px', textAlign: 'center', fontSize: '11.5px', color: '#94a3b8' }}>
                    Tidak ditemukan
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalPicDropdown;
